import * as Yup from 'yup'
import { FormikHelpers } from 'formik'
import { useToast } from 'components/Toast'
import { useMutation } from 'hooks/useMutation'

import joinErrorPaths from 'lib/error-handling/join-error-paths'

import ADJUST_STOCK_ON_HAND_MUTATION from './graphql/AdjustStockOnHandMutation.graphql'
import {
  AdjustStockOnHandMutation,
  AdjustStockOnHandMutationVariables
} from './graphql/__generated__/AdjustStockOnHandMutation'

const validationSchema = Yup.object().shape({
  adjustBy: Yup.number().integer(),
  newStockOnHand: Yup.number().integer().typeError('The field should be a number')
})

type AdjustStockOnHandArguments = {
  adjustBy: number
  newStockOnHand: number
}

type UseAdjustStockOnHandModalProps = {
  varietyId: string
  stockOnHand: number
  closeModal: () => void
}

const useAdjustStockOnHandModal = ({ varietyId, stockOnHand, closeModal }: UseAdjustStockOnHandModalProps) => {
  const [showToast] = useToast()

  const initialValues = {
    adjustBy: 0,
    newStockOnHand: stockOnHand
  } as AdjustStockOnHandArguments

  const [updateStockonHand] = useMutation<AdjustStockOnHandMutation, AdjustStockOnHandMutationVariables>(
    ADJUST_STOCK_ON_HAND_MUTATION,
    {
      onCompleted: data => {
        if (data.adjustInventory.success) {
          closeModal()
          showToast({ kind: 'success', message: 'Successfully updated stock on hand' })
        }
      },
      onError: error => {
        console.error('Failed to update stock on hand', error)
      }
    }
  )

  const onSubmit = async (
    values: AdjustStockOnHandArguments,
    { setSubmitting, setFieldError }: FormikHelpers<AdjustStockOnHandArguments>
  ) => {
    setSubmitting(true)

    const attributes = validationSchema.cast(values) as AdjustStockOnHandArguments

    const result = await updateStockonHand({
      variables: {
        input: {
          varietyId: varietyId,
          stockOnHand: attributes.newStockOnHand
        }
      },
      update: (cache, result) => {
        const varietyId = result.data?.adjustInventory.variety?.id
        const catalogId = result.data?.adjustInventory.variety?.catalogId
        if (result.data?.adjustInventory.success && varietyId) {
          cache.evict({ id: `Variety:${varietyId}`, fieldName: 'inventoryHistories' })
          // Due to SOH modification, changes in sorting & filtering, this ensures that products are listed correctly
          cache.evict({ id: `Catalog:${catalogId}`, fieldName: 'draftProducts' })
          cache.gc()
        }
      }
    })

    const errors = joinErrorPaths(result.data?.adjustInventory.errors)
    errors?.forEach(e => (e.path && e.message ? setFieldError(e.path, e.message) : ''))
    errors?.forEach(e => (e.path && e.message ? showToast({ kind: 'error', message: e.message }) : ''))

    setSubmitting(false)
  }

  return {
    initialValues,
    validationSchema,
    onSubmit
  }
}

export default useAdjustStockOnHandModal
